import type { ConsoleRole } from '@plato/shared';
import { useState } from 'react';
import { ActionError, ReadOnlyNote, ShownOnce, useAction } from '../../components/action.tsx';
import { DataTable } from '../../components/data-table.tsx';
import { formatDate } from '../../components/format.ts';
import { Loaded } from '../../components/page.tsx';
import { useConsole, useHasRole, useReloadableTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../../data/console-data.ts';
import type { InviteRow, MemberRow } from '../../data/rows.ts';

const loadTeam = (data: ConsoleData, tenant: TenantSummary) => data.getTeam(tenant.id);

const ROLES: ReadonlyArray<{ value: ConsoleRole; label: string; detail: string }> = [
  { value: 'admin', label: 'Admin', detail: 'everything, including keys, sites and the team' },
  { value: 'editor', label: 'Editor', detail: 'the assistant settings, tools and evaluations' },
  { value: 'viewer', label: 'Viewer', detail: 'look at everything, change nothing' },
];

export function TeamPage() {
  const { query, reload } = useReloadableTenantQuery(loadTeam);
  const canEdit = useHasRole('admin');
  return (
    <>
      {!canEdit && <ReadOnlyNote needs="admin" />}
      {canEdit && <InviteForm onInvited={reload} />}
      <Loaded query={query}>
        {(team) => (
          <>
            <Members members={team.members} canEdit={canEdit} onChanged={reload} />
            {team.invites.length > 0 && (
              <Invites invites={team.invites} canEdit={canEdit} onChanged={reload} />
            )}
          </>
        )}
      </Loaded>
      <dl className="role-legend">
        {ROLES.map((role) => (
          <div key={role.value}>
            <dt>{role.label}</dt>
            <dd>{role.detail}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}

function InviteForm({ onInvited }: { onInvited: () => void }) {
  const { data, tenant } = useConsole();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ConsoleRole>('editor');
  const [link, setLink] = useState<string | null>(null);
  const action = useAction();
  return (
    <section>
      <h2>Invite someone</h2>
      {link !== null && (
        <ShownOnce
          label="Send this link to them yourself. It works once, for an account signed in with that email."
          value={link}
          onDismiss={() => setLink(null)}
        />
      )}
      <form
        className="row wrap inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          void action.run(async () => {
            const created = await data.inviteMember(tenant.id, email, role);
            setLink(created.link);
            setEmail('');
            onInvited();
          });
        }}
      >
        <label>
          Email
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label>
          Role
          <select value={role} onChange={(e) => setRole(e.target.value as ConsoleRole)}>
            {ROLES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="primary" disabled={action.pending}>
          Create invite link
        </button>
      </form>
      <ActionError error={action.error} />
    </section>
  );
}

function Members(props: {
  members: readonly MemberRow[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { data, tenant } = useConsole();
  const action = useAction();
  const run = (change: () => Promise<void>) =>
    void action.run(change).then((ok) => ok && props.onChanged());
  return (
    <>
      <DataTable
        caption="Members"
        columns={[
          { header: 'Name', cell: (row) => row.name },
          { header: 'Email', cell: (row) => row.email ?? '—' },
          {
            header: 'Role',
            cell: (row) =>
              props.canEdit ? (
                <select
                  aria-label={`Role of ${row.name}`}
                  value={row.role}
                  disabled={action.pending}
                  onChange={(e) =>
                    run(() => data.setMemberRole(tenant.id, row.id, e.target.value as ConsoleRole))
                  }
                >
                  {ROLES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                row.role
              ),
          },
          { header: 'Joined', cell: (row) => formatDate(row.joinedAt) },
          ...(props.canEdit
            ? [
                {
                  header: 'Actions',
                  cell: (row: MemberRow) => (
                    <button
                      type="button"
                      className="danger"
                      disabled={action.pending}
                      onClick={() => run(() => data.removeMember(tenant.id, row.id))}
                    >
                      Remove
                    </button>
                  ),
                },
              ]
            : []),
        ]}
        rows={props.members}
      />
      {props.members.length === 0 && <p className="muted">No members yet.</p>}
      <ActionError error={action.error} />
    </>
  );
}

function Invites(props: {
  invites: readonly InviteRow[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { data, tenant } = useConsole();
  const action = useAction();
  return (
    <>
      <DataTable
        caption="Invites not yet accepted"
        columns={[
          { header: 'Email', cell: (row) => row.email },
          { header: 'Role', cell: (row) => row.role },
          { header: 'Expires', cell: (row) => formatDate(row.expiresAt) },
          ...(props.canEdit
            ? [
                {
                  header: 'Actions',
                  cell: (row: InviteRow) => (
                    <button
                      type="button"
                      className="quiet"
                      disabled={action.pending}
                      onClick={() =>
                        void action
                          .run(() => data.revokeInvite(tenant.id, row.id))
                          .then((ok) => ok && props.onChanged())
                      }
                    >
                      Revoke
                    </button>
                  ),
                },
              ]
            : []),
        ]}
        rows={props.invites}
      />
      <ActionError error={action.error} />
    </>
  );
}
